import { useEffect, useState } from 'react';
import { useDebounce } from '@hooks/useDebounce';

import {
  type NpiExpectedType,
  type NpiValidationStatus,
  useValidateNpiMutation,
  type ValidateNpiResponse,
} from '../store/api';
import { isValidNpi, isValidNpiFormat } from '../utils/npiFormat';

export type NpiFeedbackSeverity = 'success' | 'warning' | 'error' | 'info';

export interface NpiFeedback {
  /** null while idle/empty — nothing to show. */
  severity: NpiFeedbackSeverity | null;
  status: NpiValidationStatus | null;
  message: string;
  registryName?: string | null;
  /** NUCC code of the NPPES record's primary taxonomy, when a record was found. */
  primaryTaxonomyCode?: string | null;
  isChecking: boolean;
}

const EMPTY: NpiFeedback = {
  severity: null,
  status: null,
  message: '',
  isChecking: false,
};

const SEVERITY_BY_STATUS: Record<NpiValidationStatus, NpiFeedbackSeverity> = {
  valid: 'success',
  wrong_type: 'warning',
  not_found: 'warning',
  malformed: 'error',
  unverified: 'info',
};

function toFeedback(res: ValidateNpiResponse): NpiFeedback {
  return {
    severity: SEVERITY_BY_STATUS[res.status],
    status: res.status,
    message: res.message,
    registryName: res.registry_name,
    primaryTaxonomyCode: res.primary_taxonomy_code,
    isChecking: false,
  };
}

/**
 * Validate an NPI for a given field, producing advisory (never blocking)
 * feedback. Runs an instant local format/checksum check, then a debounced
 * server lookup that distinguishes individual vs organization NPIs.
 *
 * Validates on mount and whenever the value settles, so a saved mismatch stays
 * visible until corrected — not just while actively typing.
 */
export function useNpiValidation(
  npi: string | null | undefined,
  expectedType: NpiExpectedType,
): NpiFeedback {
  const value = (npi ?? '').trim();
  const debounced = useDebounce(value, 500);
  const [validate] = useValidateNpiMutation();
  const [result, setResult] = useState<{
    value: string;
    expectedType: NpiExpectedType;
    feedback: NpiFeedback;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Cancel the previous value immediately, including during the debounce window.
    if (value !== debounced || !isValidNpi(value)) return;
    const setFeedback = (feedback: NpiFeedback) => {
      if (!cancelled) setResult({ value, expectedType, feedback });
    };
    setFeedback({ ...EMPTY, isChecking: true });
    validate({
      validateNpiRequest: { npi: debounced, expected_type: expectedType },
    })
      .unwrap()
      .then(res => {
        if (!cancelled) setFeedback(toFeedback(res));
      })
      .catch(() => {
        if (!cancelled) {
          setFeedback({
            severity: 'info',
            status: 'unverified',
            message: 'Couldn’t check this NPI right now. It looks well-formed.',
            isChecking: false,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [value, debounced, expectedType, validate]);

  if (!value) return EMPTY;
  if (!isValidNpi(value)) {
    return {
      severity: 'error',
      status: 'malformed',
      message: !isValidNpiFormat(value)
        ? 'NPI must be 10 digits and start with 1 or 2.'
        : 'This NPI fails its checksum — double-check for a typo.',
      isChecking: false,
    };
  }
  if (value !== debounced || result?.value !== value || result.expectedType !== expectedType) {
    return { ...EMPTY, isChecking: true };
  }
  return result.feedback;
}
